(() => {
  if (window.__vareumShippingPrewarm || window.Shopify?.designMode) return;
  const endpoint = 'https://vareum-shipping.onrender.com/storefront/prepare';
  const configNode = document.getElementById('vareum-shipping-prewarm-config');
  let config;
  try { config = JSON.parse(configNode?.textContent || ''); } catch { return; }
  if (config.endpoint !== endpoint || !['https://vareum.com', 'https://www.vareum.com'].includes(window.location.origin)) return;
  window.__vareumShippingPrewarm = true;

  const storageKey = 'vareum.shipping.prewarm.v2';
  const readyTTL = 6 * 60 * 60 * 1000;
  const errorTTL = 5 * 60 * 1000;
  const requestWindow = 10000;
  const retryDelays = [8000, 20000, 45000, 60000];
  const pending = new Map();
  const inFlight = new Set();
  let saved = {records: {}, requestTimes: []};
  let timer, idle, running = false;
  const idOf = value => /^(?:[1-9][0-9]{0,29})$/.test(String(value ?? '')) ? String(value) : null;

  try {
    const previous = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
    if (previous && typeof previous.records === 'object' && !Array.isArray(previous.records)) {
      const now = Date.now();
      saved.records = Object.fromEntries(Object.entries(previous.records).filter(([id, record]) => idOf(id)
        && ['ready', 'preparing', 'unavailable'].includes(record?.status)
        && Number.isFinite(record.until) && record.until > now && record.until <= now + readyTTL
        && Number.isSafeInteger(record.attempts) && record.attempts >= 0 && record.attempts <= retryDelays.length).slice(-100));
      if (Array.isArray(previous.requestTimes)) saved.requestTimes = previous.requestTimes
        .filter(at => Number.isFinite(at) && at <= now && at > now - requestWindow).sort((a, b) => a - b).slice(-6);
    }
  } catch {}

  function persist() {
    const now = Date.now();
    saved.records = Object.fromEntries(Object.entries(saved.records).filter(([, record]) => record.until > now));
    try { window.sessionStorage.setItem(storageKey, JSON.stringify({...saved,
      records: Object.fromEntries(Object.entries(saved.records).slice(-100))})); } catch {}
  }

  function nextRequestAt(now) {
    saved.requestTimes = saved.requestTimes.filter(at => at > now - requestWindow);
    return saved.requestTimes.length >= 6 ? saved.requestTimes[0] + requestWindow : now;
  }

  function schedule() {
    window.clearTimeout(timer);
    if (idle !== undefined) { window.cancelIdleCallback?.(idle); idle = undefined; }
    if (running || !pending.size || document.visibilityState === 'hidden') return;
    let due = Infinity;
    for (const job of pending.values()) due = Math.min(due, job.due);
    due = Math.max(due, nextRequestAt(Date.now()));
    timer = window.setTimeout(() => {
      if (window.requestIdleCallback) idle = window.requestIdleCallback(() => { idle = undefined; flush(); }, {timeout: 1000});
      else flush();
    }, Math.max(300, due - Date.now()));
  }

  function enqueue(values, urgent = false) {
    const now = Date.now();
    for (const value of values) {
      const id = idOf(value);
      if (!id || inFlight.has(id)) continue;
      if (pending.has(id)) continue;
      const record = saved.records[id];
      if (record?.until > now && record.status !== 'preparing') continue;
      const preparing = record?.status === 'preparing';
      pending.set(id, {due: preparing && record.until > now ? record.until : now,
        attempts: preparing ? record.attempts : 0, priority: preparing ? 1 : 0});
    }
    if (urgent) flush();
    else schedule();
  }

  async function flush() {
    if (running || document.visibilityState === 'hidden') return;
    const now = Date.now();
    if (nextRequestAt(now) > now) { schedule(); return; }
    const ids = [...pending].filter(([, job]) => job.due <= now)
      .sort((a, b) => a[1].priority - b[1].priority).slice(0, 10).map(([id]) => id);
    if (!ids.length) { schedule(); return; }
    const previousAttempts = new Map(ids.map(id => [id, pending.get(id).attempts]));
    running = true;
    saved.requestTimes.push(now);
    for (const id of ids) { pending.delete(id); inFlight.add(id); }
    persist();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(endpoint, {
        method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store', redirect: 'error',
        referrerPolicy: 'no-referrer', keepalive: true, signal: controller.signal,
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({items: ids.map(variantId => ({variantId}))}),
      });
      if (!response.ok || response.headers?.get('content-type')?.split(';')[0].trim().toLowerCase() !== 'application/json') throw new Error();
      const result = await response.json();
      const statuses = ['ready', 'preparing', 'unavailable'];
      if (!statuses.includes(result?.status) || !Array.isArray(result.items) || result.items.length !== ids.length) throw new Error();
      const entries = new Map();
      for (const item of result.items) {
        const id = idOf(item?.variantId);
        if (!id || !ids.includes(id) || entries.has(id) || !statuses.includes(item.status)) throw new Error();
        entries.set(id, item);
      }
      for (const id of ids) {
        const entry = entries.get(id);
        const attempts = previousAttempts.get(id);
        if (entry?.status === 'ready') saved.records[id] = {status: 'ready', until: Date.now() + readyTTL, attempts: 0};
        else if (entry?.status === 'preparing' && attempts < retryDelays.length) {
          const until = Date.now() + retryDelays[attempts];
          saved.records[id] = {status: 'preparing', until, attempts: attempts + 1};
          pending.set(id, {due: until, attempts: attempts + 1, priority: 1});
        } else saved.records[id] = {status: 'unavailable', until: Date.now() + errorTTL, attempts: 0};
      }
    } catch {
      for (const id of ids) saved.records[id] = {status: 'unavailable', until: Date.now() + errorTTL, attempts: 0};
    } finally {
      window.clearTimeout(timeout);
      for (const id of ids) inFlight.delete(id);
      running = false;
      persist();
      schedule();
    }
  }

  function productForm(form) {
    if (form?.tagName !== 'FORM' || String(form.method).toLowerCase() !== 'post') return false;
    try {
      const action = new URL(form.getAttribute('action') || '', document.baseURI);
      return action.origin === window.location.origin && /\/cart\/add(?:\.js)?\/?$/.test(action.pathname);
    } catch { return false; }
  }

  function cartIds(items) {
    return Array.isArray(items) ? items.filter(item => item?.requires_shipping !== false)
      .map(item => item.variant_id ?? item.id) : [];
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (productForm(form)) enqueue([form.querySelector('[name="id"]')?.value], true);
  }, true);
  document.addEventListener('change', event => {
    const field = event.target;
    if (field?.name === 'id' && productForm(field.form)) enqueue([field.value]);
  }, true);
  document.addEventListener('variant:changed', event => {
    const variant = event.detail?.variant;
    if (variant?.requires_shipping !== false) enqueue([variant?.id]);
  });
  document.addEventListener('product-ajax:added', event => {
    const product = event.detail?.product;
    enqueue(cartIds(Array.isArray(product?.items) ? product.items : product ? [product] : []));
  });
  document.addEventListener('cart:updated', event => enqueue(cartIds(event.detail?.cart?.items)));
  document.addEventListener('visibilitychange', schedule);
  enqueue([config.productVariantId, ...(Array.isArray(config.cartVariantIds) ? config.cartVariantIds : [])]);
})();
